/**
 * What a candidate says they want from a job: which hours, which sectors, how many hours a
 * week, and whether they are looking at all.
 *
 * The vocabulary here is deliberately the SAME one job ads are posted with — a candidate
 * wanting "SHIFT" and an ad offering "SHIFT" are the same token, so the two can be compared
 * later without a translation table nobody remembers to update. The job-posting wizard reads
 * its Ажлын цаг options from this file for exactly that reason (see employerJobs.js).
 *
 * Sectors are NOT listed here. The five live in the database and reach the client through
 * useSectors(); a copy in this file would be a fourth place for them to drift.
 */

/**
 * The three work schedules, in the order they are offered.
 *
 * `HOURLY` rather than the backend's older `PART_TIME` is not an oversight — PART_TIME drives
 * the separate part-time shift app, whose checkout reads the salary as an hourly rate. See
 * DiilEmployerConstants.WorkSchedule on the server.
 */
export const SCHEDULE_OPTIONS = [
  { code: "FULL_TIME", label: "Бүтэн цаг" },
  { code: "HOURLY", label: "Цагийн" },
  { code: "SHIFT", label: "Ээлжийн" },
];

/**
 * Whether a set of chosen schedules is one where "how many days a week" is a real question.
 *
 * Full-time answers it by definition — five or six days is what the words mean — so the
 * question is only asked of someone who picked hourly or shift work and nothing else. Asking
 * it of a full-time candidate produced an answer nobody could act on, and one more field on
 * a form they had already finished.
 */
export const asksDaysPerWeek = (schedules = []) =>
  schedules.length > 0
  && !schedules.includes("FULL_TIME")
  && schedules.some((code) => code === "HOURLY" || code === "SHIFT");

/** 1…7. A week has seven days, so a free-number box here can only be wrong. */
export const DAYS_PER_WEEK_OPTIONS = [1, 2, 3, 4, 5, 6, 7];

/** Label -> code, for the posting wizard, which works in labels. */
export const SCHEDULE_LABEL_TO_CODE = Object.fromEntries(
  SCHEDULE_OPTIONS.map((o) => [o.label, o.code])
);

/** The label for a stored code, or the code itself if it is one we no longer offer. */
export const scheduleLabel = (code) =>
  SCHEDULE_OPTIONS.find((o) => o.code === code)?.label || code;

/**
 * The stored comma-separated form as a list.
 *
 * Tolerant on purpose: these columns hold whatever was written when the row was last
 * touched, including by an AI import that may have used spacing of its own.
 */
export const parseCsv = (value) =>
  String(value || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

/** A list back to the stored form. Empty becomes "" — the server reads that as "none". */
export const toCsv = (list) => (list || []).filter(Boolean).join(",");

/**
 * Add a hand-typed sector to the list.
 *
 * The five curated sectors cover most people and nobody else: a vet, a translator and a
 * driving instructor all had to pick "Оффис" or say nothing. So the field takes free text as
 * well, stored in the same comma-separated column — `sectorLabel` already falls back to
 * showing an unrecognised value as written.
 *
 * Typing the name of a sector that already exists stores THAT sector's key rather than a
 * second, look-alike free-text copy of it; otherwise a list could hold both "Оффис" and
 * "office" and mean one thing.
 *
 * Commas are stripped: the column is comma-separated, so a comma inside one value would come
 * back as two.
 *
 * @param {string[]} list what is selected now
 * @param {string} text what was typed
 * @param {Array<{key: string, label?: string, title?: string}>} [sectors] the curated list
 * @returns {string[]} the new selection — unchanged when the text is empty or already there
 */
export const addCustomSector = (list = [], text, sectors = []) => {
  const value = String(text || "").replace(/,/g, " ").trim().replace(/\s+/g, " ");
  if (!value) return list;

  const match = sectors.find((s) => [s.key, s.label, s.title]
    .filter(Boolean)
    .some((name) => String(name).toLowerCase() === value.toLowerCase()));
  const stored = match ? match.key : value;

  return list.some((v) => String(v).toLowerCase() === stored.toLowerCase())
    ? list
    : [...list, stored];
};

/** The selected values that are not one of the curated sectors — typed in by hand. */
export const customSectors = (list = [], sectors = []) =>
  list.filter((key) => !sectors.some((s) => s.key === key));

/** Whether a list contains a value, for chip toggles. */
export const toggleIn = (list, value) =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
